import { Meteor } from 'meteor/meteor';

/**
 * A super user's trip through somebody else's account, and the way back out of it.
 *
 * Impersonation replaces the browser's login token with the target user's, which is why going back needs
 * the original token kept somewhere first — `beginImpersonation` has to run BEFORE the login call, while
 * the super user's own token is still the one in storage. The token sits beside the one Meteor already
 * keeps there under its own key, so this adds a second copy rather than a new kind of secret.
 */
export interface Impersonation {
  /** The super user's own resume token, as it was before the impersonation login replaced it. */
  token: string;
  tokenExpires?: number;
  /** The super user's own id, which is how the banner tells "still impersonating" from "already back". */
  userId: string;
  /** Who is being impersonated, for the banner. */
  label: string;
  /** Where to land once the super user is themselves again. */
  returnPath: string;
  startedAt: number;
}

const KEY = 'luminia.impersonation';
const METEOR_TOKEN = 'Meteor.loginToken';
const METEOR_TOKEN_EXPIRES = 'Meteor.loginTokenExpires';

/** Meteor's own storage wrapper on the client, or nothing at all on the server and under jest. */
const storage = (): Storage | undefined => (Meteor as any)._localStorage || (typeof localStorage === 'undefined' ? undefined : localStorage);

/**
 * The impersonation this browser is in the middle of, if any.
 * @returns the stored impersonation, or undefined when there is none or the stored value is unusable
 */
export const readImpersonation = (): Impersonation | undefined => {
  try {
    const raw = storage()?.getItem(KEY);
    if (!raw) return undefined;
    const value = JSON.parse(raw) as Impersonation;
    if (!value?.token || !value?.userId) return undefined;
    return value;
  }
  catch {
    return undefined;
  }
};

/** Forgets the impersonation, so the banner stops offering a way back. */
export const clearImpersonation = () => {
  try {
    storage()?.removeItem(KEY);
  }
  catch {
    // A browser that refuses storage cannot be impersonating either: there was nothing to clear.
  }
};

/**
 * Remembers who the super user is before they log in as somebody else. Call this BEFORE the login.
 * @param arg - who is being impersonated, and where to return to afterwards
 * @returns whether a way back was stored; false means the login token was not readable
 */
export const beginImpersonation = (arg: { label: string, returnPath: string }): boolean => {
  try {
    const store = storage();
    const token = store?.getItem(METEOR_TOKEN);
    const userId = Meteor.userId();
    if (!store || !token || !userId) return false;
    const expires = store.getItem(METEOR_TOKEN_EXPIRES);
    const impersonation: Impersonation = {
      token,
      tokenExpires: expires ? new Date(expires).getTime() : undefined,
      userId,
      label: arg.label,
      returnPath: arg.returnPath,
      startedAt: Date.now(),
    };
    store.setItem(KEY, JSON.stringify(impersonation));
    return true;
  }
  catch {
    return false;
  }
};

/**
 * Whether an impersonation is live for the user the browser is currently logged in as. A stash whose user
 * IS the current user has already been returned from, and a logged-out browser has nothing to return to.
 * @param impersonation - the stored impersonation, if any
 * @param currentUserId - the id of the user the browser is logged in as
 * @returns true while somebody else's account is on screen
 */
export const isImpersonating = (impersonation?: Impersonation, currentUserId?: string | null): boolean =>
  !!impersonation && !!currentUserId && impersonation.userId !== currentUserId;

/**
 * Logs back in as the super user who started the impersonation and clears the stash either way: a token
 * that no longer works is not worth keeping, and leaving it would strand the banner on screen.
 * @returns the path to land on, or undefined when there was no way back to take
 */
export const endImpersonation = (): Promise<string | undefined> => new Promise((resolve, reject) => {
  const impersonation = readImpersonation();
  if (!impersonation) return resolve(undefined);
  (Meteor as any).loginWithToken(impersonation.token, (error?: Error) => {
    clearImpersonation();
    if (error) return reject(error);
    resolve(impersonation.returnPath || '/');
  });
});

/** Where the banner has been dragged to, in viewport pixels from the top left. */
export interface BannerPosition {
  left: number;
  top: number;
}

const POSITION_KEY = 'luminia.impersonationBannerPosition';

/**
 * Holds a position inside the viewport, so a window that has since been resized — or a stored position
 * from a larger screen — cannot leave the banner off the edge where nobody can reach it.
 * @param position - the desired position
 * @param size - the banner's rendered size
 * @param viewport - the window's inner size
 * @param margin - how close to the edge the banner may sit
 * @returns the position, moved inside the viewport if it was outside
 */
export const clampBannerPosition = (
  position: BannerPosition,
  size: { width: number, height: number },
  viewport: { width: number, height: number },
  margin = 8,
): BannerPosition => ({
  left: Math.min(Math.max(margin, position.left), Math.max(margin, viewport.width - size.width - margin)),
  top: Math.min(Math.max(margin, position.top), Math.max(margin, viewport.height - size.height - margin)),
});

/**
 * The position the banner was last dragged to in this browser.
 * @returns the stored position, or undefined to leave it in its default corner
 */
export const readBannerPosition = (): BannerPosition | undefined => {
  try {
    const raw = storage()?.getItem(POSITION_KEY);
    if (!raw) return undefined;
    const value = JSON.parse(raw) as BannerPosition;
    if (!Number.isFinite(value?.left) || !Number.isFinite(value?.top)) return undefined;
    return { left: value.left, top: value.top };
  }
  catch {
    return undefined;
  }
};

/** Remembers where the banner was dragged to, so it stays put across pages and reloads. */
export const writeBannerPosition = (position: BannerPosition) => {
  try {
    storage()?.setItem(POSITION_KEY, JSON.stringify(position));
  }
  catch {
    // Not worth surfacing: the banner simply starts in its default corner next time.
  }
};
